// Candidate-listing queries over the arena /v1/issuers endpoint —
// status/search/sort/pagination filter building plus the eligibility rule,
// for qualification-style provider listings. Built on @/modules/arena-api
// (fetch + Issuer DTO — consumers take the Issuer type from there).

import {
  arenaFetch,
  IssuerSchema,
  IssuersPageSchema,
  type Issuer,
} from '@/modules/arena-api';

export type Status = 'all' | 'eligible' | 'not_eligible' | 'funded';
export type SortBy = 'stake' | 'accuracy';
export type SortDir = 'asc' | 'desc';
// The two prediction-volume groups of the candidate listing, split at
// LOW_VOLUME_THRESHOLD. fetchListingPage never applies a volume clause to
// 'funded' — allocated providers show regardless of prediction count.
export type Volume = 'high' | 'low';

// Which issuers a listing covers. fundedAddresses is the wallets holding a
// live allocation, resolved by the caller from the allocations list: 'funded'
// lists exactly those wallets, every candidate status excludes them
// (graduated out of the candidate pool). /v1/issuers has no filter over the
// allocations relation and its allocation_amount/allocation_date columns
// are not maintained for new allocations, so the wallets are named one by
// one — exact-case equality, the filter is case-sensitive and both endpoints
// serve the same stored EIP-55 string.
type Scope = {
  status?: Status | undefined;
  fundedAddresses: readonly string[];
};

export type FetchIssuersParams = Scope & {
  search?: string | undefined;
  sortBy?: SortBy | undefined;
  sortDir?: SortDir | undefined;
  page?: number | undefined; // 1-based
  pageSize?: number | undefined; // defaults to PAGE_SIZE
  volume?: Volume | undefined;
};

// The volume-group boundary: 'high' = total_predictions ≥ 10, 'low' = < 10
// or null (the listing's "Candidates with less than 10 predictions" split;
// never-predicted issuers belong to the low group).
export const LOW_VOLUME_THRESHOLD = 10;

export const PAGE_SIZE = 10;

// The eligibility rule: at least 30 predictions AND accuracy outside the
// (0.4, 0.6) mid-band — both boundaries INCLUSIVE on the eligible side
// (0.4 and 0.6 are eligible). null accuracy is never eligible; null
// prediction counts read as 0. The param is deliberately a widened
// structural shape so both the parsed arena Issuer and nullable-field DTO
// variants fit.
export function isEligible(i: {
  total_predictions: number | null;
  accuracy: number | null;
}): boolean {
  if ((i.total_predictions ?? 0) < 30) return false;
  if (i.accuracy == null) return false;
  return i.accuracy <= 0.4 || i.accuracy >= 0.6;
}

function buildSort(sortBy: SortBy, sortDir: SortDir) {
  const field = sortBy === 'accuracy' ? 'accuracy' : 'active_stake_on_user';
  return `${field}:${sortDir}`;
}

// A funded scope with no allocated wallet is empty by construction — the
// membership filter has nothing to name — so its readers answer empty
// without a request.
function isEmptyScope(
  status: Status,
  fundedAddresses: readonly string[],
): boolean {
  return status === 'funded' && fundedAddresses.length === 0;
}

// Clause values follow the live-verified wire shapes: STRINGS for accuracy
// ("0.4"), plain JS numbers for total_predictions (30, 10). The funded
// clause is wallet membership (`in`), the candidate clause its negation
// (`notIn`); negative filters drop null rows server-side (SQL semantics,
// verified live 2026-09-28 on total_predictions), which costs nothing on
// address — the spec declares it non-nullable, and filtering it against
// null is a 500. 'eligible'/'not_eligible' mirror isEligible server-side.
// A single clause is sent bare — the AND wrapper appears only when
// combining — and no clause at all (a candidate listing with no allocated
// wallet) is null: the filter param is omitted.
function buildFilter(
  status: Status,
  fundedAddresses: readonly string[],
  search: string | undefined,
  volume: Volume | undefined,
): Record<string, unknown> | null {
  const clauses: Record<string, unknown>[] = [];
  if (status === 'funded') {
    clauses.push({ address: { in: fundedAddresses } });
  } else if (fundedAddresses.length > 0) {
    clauses.push({ address: { notIn: fundedAddresses } });
  }
  if (status === 'eligible') {
    clauses.push({ total_predictions: { gte: 30 } });
    clauses.push({
      OR: [{ accuracy: { lte: '0.4' } }, { accuracy: { gte: '0.6' } }],
    });
  } else if (status === 'not_eligible') {
    // isEligible's negation with the null arms explicit: comparison
    // operators exclude nulls server-side (same behavior the volume clause
    // below works around), so never-predicted issuers (total_predictions
    // null) and null-accuracy issuers with ≥30 predictions — both
    // not-eligible client-side — matched NEITHER eligibility tab without
    // these arms.
    clauses.push({
      OR: [
        { total_predictions: { lt: 30 } },
        { total_predictions: { equals: null } },
        { accuracy: { equals: null } },
        {
          AND: [{ accuracy: { gt: '0.4' } }, { accuracy: { lt: '0.6' } }],
        },
      ],
    });
  }
  if (volume === 'high') {
    clauses.push({ total_predictions: { gte: LOW_VOLUME_THRESHOLD } });
  } else if (volume === 'low') {
    // Comparison operators exclude nulls server-side (same behavior the
    // allocation clause above works around), so never-predicted issuers
    // (total_predictions null) must be ORed in explicitly — without this
    // they matched NEITHER slice and vanished from the combined listing
    // (5 of 47 live issuers). Client-side they already read as the low
    // group (safeFloat null→0).
    clauses.push({
      OR: [
        { total_predictions: { lt: LOW_VOLUME_THRESHOLD } },
        { total_predictions: { equals: null } },
      ],
    });
  }
  if (search != null && search !== '') {
    clauses.push({
      OR: [
        { username: { contains: search } },
        { address: { contains: search } },
      ],
    });
  }
  const [first, ...rest] = clauses;
  if (first === undefined) return null;
  return rest.length === 0 ? first : { AND: clauses };
}

function fetchIssuersAt(opts: {
  limit: number;
  offset: number;
  sortBy: SortBy;
  sortDir: SortDir;
  status: Status;
  fundedAddresses: readonly string[];
  search: string | undefined;
  volume: Volume | undefined;
}) {
  const query = new URLSearchParams();
  query.set('limit', String(opts.limit));
  query.set('offset', String(opts.offset));
  query.set('sort', buildSort(opts.sortBy, opts.sortDir));
  const filter = buildFilter(
    opts.status,
    opts.fundedAddresses,
    opts.search,
    opts.volume,
  );
  if (filter !== null) query.set('filter', JSON.stringify(filter));
  return arenaFetch(`/v1/issuers?${query.toString()}`, IssuersPageSchema);
}

// One page of a listing. limit/offset derive from page/pageSize (1-based
// page, pageSize defaults to PAGE_SIZE); sort maps 'stake' →
// active_stake_on_user, 'accuracy' → accuracy, default stake:desc. An empty
// funded scope answers an empty page without a request. Parse fail-open and
// error semantics are arena-api's.
export async function fetchIssuers(params: FetchIssuersParams): Promise<{
  data: Issuer[];
  total: number;
  limit: number;
  offset: number;
}> {
  const {
    // Stryker disable next-line StringLiteral: equivalent — every value outside 'funded'/'eligible'/'not_eligible' takes buildFilter's candidate branch, same as 'all'
    status = 'all',
    fundedAddresses,
    search,
    // Stryker disable next-line StringLiteral: equivalent — buildSort maps every non-'accuracy' value to active_stake_on_user, same as 'stake'
    sortBy = 'stake',
    sortDir = 'desc',
    page = 1,
    pageSize = PAGE_SIZE,
    volume,
  } = params;
  const limit = pageSize;
  const offset = (page - 1) * pageSize;
  if (isEmptyScope(status, fundedAddresses)) {
    return { data: [], total: 0, limit, offset };
  }
  return fetchIssuersAt({
    limit,
    offset,
    sortBy,
    sortDir,
    status,
    fundedAddresses,
    search,
    volume,
  });
}

// One PAGE_SIZE window over the combined candidate listing: the whole
// high-volume group first, then the whole low-volume group, ranks running
// continuously across the seam (row rank = window start + index + 1; the
// group boundary sits at row index highTotal − window start when that
// falls inside the window). Candidate statuses issue two sequential
// requests — the high slice at the page window, then the low slice at the
// window's remainder. Status 'funded' is a single-request pass-through
// with NO volume clause (lowTotal is always 0 there). Fail-open parse
// semantics are arena-api's — a raw fallback surfaces to the caller
// unchanged.
export async function fetchListingPage(
  params: Scope & {
    search?: string | undefined;
    sortBy?: SortBy | undefined;
    sortDir?: SortDir | undefined;
    page?: number | undefined; // 1-based, PAGE_SIZE rows per window
  },
): Promise<{
  rows: Issuer[];
  // unread-field-allow: consumed structurally — the whole result is the `listing` param of deriveListingView, which reads it on its own structural type
  highTotal: number;
  // unread-field-allow: consumed structurally — the whole result is the `listing` param of deriveListingView, which reads it on its own structural type
  lowTotal: number;
  total: number;
}> {
  const {
    // Stryker disable next-line StringLiteral: equivalent — every value outside 'funded' takes the candidate two-slice path, and buildFilter treats every non-'funded'/'eligible'/'not_eligible' value as 'all'
    status = 'all',
    fundedAddresses,
    search,
    // Stryker disable next-line StringLiteral: equivalent — buildSort maps every non-'accuracy' value to active_stake_on_user, same as 'stake'
    sortBy = 'stake',
    sortDir = 'desc',
    page = 1,
  } = params;

  if (status === 'funded') {
    const funded = await fetchIssuers({
      status,
      fundedAddresses,
      search,
      sortBy,
      sortDir,
      page,
    });
    return {
      rows: funded.data,
      highTotal: funded.total,
      lowTotal: 0,
      total: funded.total,
    };
  }

  const start = (page - 1) * PAGE_SIZE;
  const high = await fetchIssuersAt({
    limit: PAGE_SIZE,
    offset: start,
    sortBy,
    sortDir,
    status,
    fundedAddresses,
    search,
    volume: 'high',
  });

  // Remaining slots on this page come from the low-volume group, whose
  // window offset is how far this page starts past the seam. When the page
  // is filled entirely by high-volume rows the low fetch still runs — limit
  // 1, rows discarded — because lowTotal feeds the combined total (the
  // offset clamps to 0 there: a full high window ends at or before the
  // seam, so start − highTotal is negative).
  const lowNeed = PAGE_SIZE - high.data.length;
  const low = await fetchIssuersAt({
    limit: Math.max(lowNeed, 1),
    offset: Math.max(0, start - high.total),
    sortBy,
    sortDir,
    status,
    fundedAddresses,
    search,
    volume: 'low',
  });

  return {
    rows: lowNeed > 0 ? [...high.data, ...low.data] : high.data,
    highTotal: high.total,
    lowTotal: low.total,
    total: high.total + low.total,
  };
}

// /v1/issuers/{id} with the SAME sort/filter as the listing: the response
// is the issuer row plus `position` — its 1-based rank within that exact
// view (for pinning a known issuer's row against the current listing).
// Against fetchListingPage's combined view, pass the issuer's own volume
// group; a 'low' issuer's combined rank = highTotal + position. null on
// 404 (id unknown or filtered out server-side) and for an empty funded
// scope, which sends no request.
export async function fetchIssuerWithPosition(
  id: string,
  params: Scope & {
    search?: string | undefined;
    sortBy?: SortBy | undefined;
    sortDir?: SortDir | undefined;
    volume?: Volume | undefined;
  },
): Promise<Issuer | null> {
  const {
    // Stryker disable next-line StringLiteral: equivalent — every value outside 'funded'/'eligible'/'not_eligible' takes buildFilter's candidate branch, same as 'all'
    status = 'all',
    fundedAddresses,
    search,
    // Stryker disable next-line StringLiteral: equivalent — buildSort maps every non-'accuracy' value to active_stake_on_user, same as 'stake'
    sortBy = 'stake',
    sortDir = 'desc',
    volume,
  } = params;
  if (isEmptyScope(status, fundedAddresses)) return null;
  const query = new URLSearchParams();
  query.set('sort', buildSort(sortBy, sortDir));
  const filter = buildFilter(status, fundedAddresses, search, volume);
  if (filter !== null) query.set('filter', JSON.stringify(filter));

  return arenaFetch(`/v1/issuers/${id}?${query.toString()}`, IssuerSchema, {
    nullOn404: true,
  });
}
